import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
// Removed problematic CookieAttributes import

// Define the structure for each favorite item
export interface FavoriteItem {
  id: string
  comment: string
  wantsTestDrive: boolean
  hasTradeIn: boolean
  interestedInFinance: boolean
  testDriveDateTime: string | null
  testDriveTime: string | null
  // Trade-in details
  tradeInMake: string | null
  tradeInModel: string | null
  tradeInYear: string | null // Could be number
  tradeInKms: string | null  // Could be number
}

// Removed separate cookieOptions constant

export const useFavoritesStore = defineStore('favorites', () => {
  const favorites = ref<FavoriteItem[]>([])

  // Check if a model ID is in favorites by checking the IDs in the objects
  const isFavorite = (modelId: string): boolean => {
    return favorites.value.some(item => item.id === modelId)
  }

  // Add a model ID to favorites with default options
  const addFavorite = (modelId: string) => {
    if (!isFavorite(modelId)) {
      favorites.value.push({
        id: modelId, 
        comment: '', 
        wantsTestDrive: false, 
        hasTradeIn: false, 
        interestedInFinance: false,
        testDriveDateTime: null,
        testDriveTime: null,
        // Initialize trade-in fields
        tradeInMake: null,
        tradeInModel: null,
        tradeInYear: null,
        tradeInKms: null
      })
    }
  }

  // Remove a favorite item by model ID
  const removeFavorite = (modelId: string) => {
    favorites.value = favorites.value.filter(item => item.id !== modelId)
  }

  // Toggle favorite status
  const toggleFavorite = (modelId: string) => {
    if (isFavorite(modelId)) {
      removeFavorite(modelId)
    } else {
      addFavorite(modelId)
    }
  }

  // Update the comment for a specific favorite item
  const updateComment = (modelId: string, comment: string) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) {
      favoriteItem.comment = comment
    }
  }

  // Get the comment for a specific model ID
  const getComment = (modelId: string): string => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    return favoriteItem ? favoriteItem.comment : ''
  }

  // --- Actions to update boolean flags ---
  const setTestDrive = (modelId: string, value: boolean) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) favoriteItem.wantsTestDrive = value
  }
  const setTradeIn = (modelId: string, value: boolean) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) favoriteItem.hasTradeIn = value
  }
  const setFinance = (modelId: string, value: boolean) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) favoriteItem.interestedInFinance = value
  }
  
  // Renamed: Action to update test drive date
  const setTestDriveDate = (modelId: string, dateTime: string | null) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) {
      favoriteItem.testDriveDateTime = dateTime
    }
  }

  // ADDED: Action to update test drive time
  const setTestDriveTime = (modelId: string, time: string | null) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) {
      favoriteItem.testDriveTime = time
    }
  }
  // ---------------------------------------

  // --- Actions to update trade-in details ---
  const setTradeInMake = (modelId: string, make: string | null) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) favoriteItem.tradeInMake = make
  }
  const setTradeInModel = (modelId: string, model: string | null) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) favoriteItem.tradeInModel = model
  }
  const setTradeInYear = (modelId: string, year: string | null) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) favoriteItem.tradeInYear = year
  }
  const setTradeInKms = (modelId: string, kms: string | null) => {
    const favoriteItem = favorites.value.find(item => item.id === modelId)
    if (favoriteItem) favoriteItem.tradeInKms = kms
  }
  // -----------------------------------------

  // --- NEW ACTION --- 
  const loadClientState = () => {
    if (process.client) {
      try {
        const storedValue = localStorage.getItem('favorites'); // Assuming key is 'favorites'
        if (storedValue) {
          const parsedState = JSON.parse(storedValue);
          // Check if the parsed state has the expected 'favorites' array
          if (parsedState && Array.isArray(parsedState.favorites)) {
             const validFavorites = parsedState.favorites.filter(
               (fav: any) => typeof fav.id === 'string' && fav.id.trim() !== ''
             );
             favorites.value = validFavorites;
             if (validFavorites.length !== parsedState.favorites.length) {
               // console.warn('[FavoritesStore] Filtered out some invalid favorite items from localStorage.');
             }
             // console.log('[FavoritesStore] Loaded state from localStorage.');
          } else {
             // console.warn('[FavoritesStore] Invalid data format in localStorage.');
             favorites.value = []; // Reset to default if format is wrong
          }
        } else {
           favorites.value = []; // Initialize as empty if nothing in storage
        }
      } catch (e) {
        // console.error('[FavoritesStore] Error loading state from localStorage:', e);
        favorites.value = []; // Reset to default on error
      }
    }
  }

  return {
    favorites,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    updateComment,
    getComment,
    setTestDrive,
    setTradeIn,
    setFinance,
    setTestDriveDate,
    setTestDriveTime,
    setTradeInMake,
    setTradeInModel,
    setTradeInYear,
    setTradeInKms,
    favoriteCount: computed(() => favorites.value.length),
    loadClientState // Expose the new action
  }
}, {
  persist: {
    storage: {
      getItem: (key: string): string | null => {
        // ONLY read from cookie on server, return null on client initially
        if (process.server) {
          const nuxtApp = useNuxtApp()
          const cookieValue = useCookie<FavoriteItem[] | null>(key).value
          return cookieValue ? JSON.stringify({ favorites: cookieValue }) : null // Match structure expected by plugin
        } else {
          // DO NOT read from localStorage here during initial load/hydration
          return null 
        }
      },
      setItem: (key: string, value: string): void => {
        // Always save to both (cookie for SSR, localStorage for client persistence)
        try {
          // Save to Cookie (Server + Client)
          const nuxtApp = useNuxtApp(); // Can be called on client too
          const parsedState = JSON.parse(value);
          if (parsedState && Array.isArray(parsedState.favorites)) {
              const currentCookieOptions = {
                path: '/',
                sameSite: 'lax' as const,
                secure: process.env.NODE_ENV === 'production',
                maxAge: 365 * 24 * 60 * 60
              };
              useCookie<FavoriteItem[]>(key, currentCookieOptions).value = parsedState.favorites;
          } else {
              // console.warn('[FavoritesStore] Attempted to save invalid state to cookie.');
          }

          // Save to LocalStorage (Client only)
          if (process.client) {
            localStorage.setItem(key, value)
          }
        } catch (e) {
          // console.error('Failed to parse/set storage value:', e)
        }
      },
    },
  },
}) 